import apiClient from './client';
import type { ApiResponse } from '../types';

export interface SelfAttendanceRecord {
  id: number;
  staff_id: number;
  full_name?: string;
  staff_code?: string;
  role?: string;
  profile_picture?: string | null;
  date: string;
  check_in_time?: string | null;
  check_in_lat?: number | null;
  check_in_lng?: number | null;
  check_in_photo?: string | null;
  check_in_location_name?: string | null;
  check_in_distance_meters?: number | null;
  check_out_time?: string | null;
  check_out_lat?: number | null;
  check_out_lng?: number | null;
  check_out_photo?: string | null;
  check_out_location_name?: string | null;
  check_out_distance_meters?: number | null;
  status: 'present' | 'late' | 'absent' | 'half_day' | 'leave';
  working_hours?: number | null;
  is_late?: number | boolean;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface TodayStatusResponse {
  staff: {
    id: number;
    full_name: string;
    role: string;
    staff_code: string;
  } | null;
  today: SelfAttendanceRecord | null;
  date: string;
}

export interface CheckInPayload {
  lat: number;
  lng: number;
  photo: string;
  location_name?: string;
}

export interface CheckOutPayload {
  lat: number;
  lng: number;
  photo: string;
  location_name?: string;
}

export interface AdminAttendanceSummary {
  total_records: number;
  present_count: number;
  late_count: number;
  absent_count: number;
  half_day_count: number;
  leave_count: number;
  flagged_count?: number;
  onsite_count?: number;
}

export interface AdminAttendanceParams {
  date?: string;
  start_date?: string;
  end_date?: string;
  staff_id?: number | string;
  status?: string;
  location_filter?: 'all' | 'on_site' | 'flagged';
  page?: number;
  limit?: number;
}

export const selfAttendanceAPI = {
  checkIn: async (payload: CheckInPayload): Promise<ApiResponse<{
    message: string;
    status: string;
    is_late: boolean;
    check_in_time: string;
    distance_meters: number;
    location_name: string | null;
  }>> => {
    const { data } = await apiClient.post('/self-attendance/check-in', payload);
    return data;
  },

  checkOut: async (payload: CheckOutPayload): Promise<ApiResponse<{
    message: string;
    check_out_time: string;
    working_hours: number;
    distance_meters: number;
    location_name: string | null;
  }>> => {
    const { data } = await apiClient.post('/self-attendance/check-out', payload);
    return data;
  },

  getMyToday: async (): Promise<ApiResponse<TodayStatusResponse>> => {
    const { data } = await apiClient.get('/self-attendance/my-today');
    return data;
  },

  getMyHistory: async (params?: { month?: string }): Promise<ApiResponse<SelfAttendanceRecord[]>> => {
    const { data } = await apiClient.get('/self-attendance/my-history', { params });
    return data;
  },

  adminGetAll: async (params?: AdminAttendanceParams): Promise<ApiResponse<SelfAttendanceRecord[]> & { summary?: AdminAttendanceSummary }> => {
    const { data } = await apiClient.get('/self-attendance/admin/all', { params });
    return data;
  },
};
